import { PaperClipOutlined } from "@ant-design/icons";
import { DataSource, DataSourceType } from "@coeff/api";
import {
  COLORS,
  MSSQL_WIRE_DATA_SOURCE_TYPES,
  MYSQL_WIRE_DATA_SOURCE_TYPES,
  POSTGRES_WIRE_DATA_SOURCE_TYPES,
} from "@coeff/utils";
import { Upload } from "antd";
import React from "react";
import styled from "styled-components";

import { Input, Option, Select, Switch, Typography } from "../../../common";
import { useMessage } from "../../../globals/contexts";
import { useAuthContext } from "../../context/AuthContext";

export enum TunnelAuthMethod {
  PrivateKey = "Private Key",
  Password = "Password",
}

export type TunnelConfig = {
  tunnel_host: string;
  tunnel_port: number;
  tunnel_username: string;
  tunnel_auth_method?: TunnelAuthMethod;
  tunnel_private_key?: string;
  tunnel_passphrase?: string;
  tunnel_password?: string;
  use_tunnel: boolean;
};

export type SSHTunnelFormError = Partial<Record<keyof TunnelConfig, string>>;

type SSHTunnelFormProps = {
  value?: Partial<TunnelConfig>;
  onChange?: (config: TunnelConfig) => void;
  error?: Partial<Record<keyof TunnelConfig, string>>;
};

const defaultConfig: TunnelConfig = {
  tunnel_host: "",
  tunnel_port: 22,
  tunnel_username: "",
  tunnel_auth_method: TunnelAuthMethod.PrivateKey,
  tunnel_private_key: "",
  tunnel_passphrase: "",
  tunnel_password: "",
  use_tunnel: false,
};

export const isTunnelEnabled = (type: DataSourceType): boolean => {
  return [
    ...Array.from(MYSQL_WIRE_DATA_SOURCE_TYPES),
    ...Array.from(POSTGRES_WIRE_DATA_SOURCE_TYPES),
    ...Array.from(MSSQL_WIRE_DATA_SOURCE_TYPES),
    "redshift",
  ].includes(type);
};

export const extractTunnelConfig = (dataSource?: DataSource): TunnelConfig => {
  return {
    use_tunnel: Boolean(dataSource?.credentials?.use_tunnel),
    tunnel_host: dataSource?.credentials?.tunnel_host ?? "",
    tunnel_port: dataSource?.credentials?.tunnel_port ?? 22,
    tunnel_username: dataSource?.credentials?.tunnel_username ?? "",
    tunnel_private_key: dataSource?.credentials?.tunnel_private_key ?? "",
    tunnel_auth_method:
      (dataSource?.credentials?.tunnel_auth_method as TunnelAuthMethod) ??
      TunnelAuthMethod.PrivateKey,
    tunnel_passphrase: dataSource?.credentials?.tunnel_passphrase ?? "",
    tunnel_password: dataSource?.credentials?.tunnel_password ?? "",
  };
};

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TunnelContainer = styled.div`
  display: block;
  width: auto;
  margin: 15px;
  margin-bottom: 24px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 15px;
`;

const FormField = styled.div`
  display: block;
  width: auto;
  border-radius: 4px;

  .coeff__input {
    height: 32px;
    margin: auto;
    border-color: ${COLORS.black15};
  }

  .coeff__select,
  .coeff__input {
    margin-top: 2px;
  }
`;

const StyledSelect = styled(Select)`
  && {
    width: 100%;

    &&.ant-select {
      margin: 0 !important;
    }

    .ant-select-selector {
      border-color: ${COLORS.black15} !important;
      height: 32px !important;

      .ant-select-selection-item,
      .ant-select-selection-placeholder {
        line-height: 28px !important;
      }
    }
  }
`;

const UploadInput = styled(Input)`
  && {
    border: none;
    box-shadow: none;
    background: none;
    padding: 0;
    margin: 0;
  }
`;

const ErrorText = styled.div`
  color: ${COLORS.red};
`;

export const isValidSSHTunnelConfig = (config: TunnelConfig) => {
  if (!config.use_tunnel) {
    return true;
  }

  if (!config.tunnel_host || isNaN(config.tunnel_port) || !config.tunnel_username) {
    return false;
  }

  if (config.tunnel_auth_method === TunnelAuthMethod.PrivateKey) {
    return !!config.tunnel_private_key;
  }

  return !!config.tunnel_password;
};

export const validateSSHConfig = (config: TunnelConfig) => {
  const error: SSHTunnelFormError = {
    tunnel_host: undefined,
    tunnel_port: undefined,
    tunnel_username: undefined,
    tunnel_private_key: undefined,
    tunnel_password: undefined,
  };
  if (!config.use_tunnel) {
    return null;
  }

  if (!config.tunnel_host) {
    error["tunnel_host"] = "SSH Host is required";
  }

  if (isNaN(config.tunnel_port)) {
    error["tunnel_port"] = "SSH Port is required";
  }

  if (!config.tunnel_username) {
    error["tunnel_username"] = "SSH Username is required";
  }

  if (config.tunnel_auth_method === TunnelAuthMethod.PrivateKey && !config.tunnel_private_key) {
    error["tunnel_private_key"] = "Private Key is required";
  }

  if (config.tunnel_auth_method === TunnelAuthMethod.Password && !config.tunnel_password) {
    error["tunnel_password"] = "Password is required";
  }

  return Object.values(error).filter(Boolean).length ? error : null;
};

export const SSHTunnelForm: React.FC<SSHTunnelFormProps> = ({
  value = defaultConfig,
  onChange = () => {},
  error = {},
}) => {
  const message = useMessage();
  const { sentryCapture } = useAuthContext();

  const config = { ...defaultConfig, ...value };

  const handleChange = (changes: Partial<TunnelConfig>) => {
    const newConfig = {
      ...config,
      ...changes,
    };

    onChange({
      tunnel_host: newConfig.tunnel_host,
      tunnel_port: newConfig.tunnel_port,
      tunnel_username: newConfig.tunnel_username,
      tunnel_auth_method: newConfig.tunnel_auth_method,
      use_tunnel: newConfig.use_tunnel,
      ...(newConfig.tunnel_auth_method === TunnelAuthMethod.PrivateKey
        ? {
            tunnel_private_key: newConfig.tunnel_private_key,
            tunnel_passphrase: newConfig.tunnel_passphrase || undefined,
          }
        : {
            tunnel_password: newConfig.tunnel_password,
          }),
    });
  };

  const handleFileUpload = async (file: File) => {
    try {
      const text = await file.text();
      handleChange({ tunnel_private_key: text });
      message?.success(`${file.name} successfully loaded`);
      return false; // Prevent default upload behavior
    } catch (error) {
      sentryCapture?.({ error, name: "ParsingFailed: Secret file error" });
      message?.error("Failed to read private key file");
      console.error("Failed to read file:", error);
      return false;
    }
  };

  return (
    <TunnelContainer>
      <FieldsContainer>
        <HeaderContainer>
          <Typography fontWeight={700}>Use SSH Tunnel</Typography>
          <Switch
            checked={config.use_tunnel}
            onChange={checked => handleChange({ use_tunnel: checked })}
            size="small"
          />
        </HeaderContainer>
        {config.use_tunnel && (
          <>
            <FormField>
              SSH Host / IP
              <Input
                placeholder="example.com"
                value={config.tunnel_host}
                onChange={e => handleChange({ tunnel_host: e.target.value })}
              />
              {error.tunnel_host && <ErrorText>{error.tunnel_host}</ErrorText>}
            </FormField>

            <FormField>
              SSH Port
              <Input
                type="number"
                placeholder="22"
                value={config.tunnel_port}
                onChange={e => handleChange({ tunnel_port: Number(e.target.value) })}
              />
              {error.tunnel_port && <ErrorText>{error.tunnel_host}</ErrorText>}
            </FormField>

            <FormField>
              SSH Username
              <Input
                value={config.tunnel_username}
                onChange={e => handleChange({ tunnel_username: e.target.value })}
              />
              {error.tunnel_username && <ErrorText>{error.tunnel_username}</ErrorText>}
            </FormField>

            <FormField>
              Authentication Method
              <StyledSelect
                value={config.tunnel_auth_method}
                onChange={method =>
                  handleChange({
                    tunnel_auth_method: method as TunnelAuthMethod,
                  })
                }
              >
                {[
                  { value: TunnelAuthMethod.PrivateKey, label: "Private Key" },
                  { value: TunnelAuthMethod.Password, label: "Password" },
                ].map(({ value, label }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </StyledSelect>
            </FormField>

            {config.tunnel_auth_method === "Private Key" ? (
              <>
                <FormField>
                  Private Key
                  <UploadInput
                    value={config.tunnel_private_key ?? ""}
                    onChange={e => handleChange({ tunnel_private_key: e.target.value })}
                    suffix={
                      <Upload showUploadList={false} beforeUpload={handleFileUpload}>
                        <PaperClipOutlined />
                      </Upload>
                    }
                    type="password"
                  />
                  {error.tunnel_private_key && <ErrorText>{error.tunnel_private_key}</ErrorText>}
                </FormField>

                <FormField>
                  Passphrase (optional)
                  <Input
                    type="password"
                    value={config.tunnel_passphrase}
                    onChange={e => handleChange({ tunnel_passphrase: e.target.value })}
                  />
                </FormField>
              </>
            ) : (
              <FormField>
                Password
                <Input
                  type="password"
                  value={config.tunnel_password}
                  onChange={e => handleChange({ tunnel_password: e.target.value })}
                />
                {error.tunnel_password && <ErrorText>{error.tunnel_password}</ErrorText>}
              </FormField>
            )}
          </>
        )}
      </FieldsContainer>
    </TunnelContainer>
  );
};
